/**
 * FieldMappingRow.tsx - Single field with mapping arrow
 * 
 * CHANGE HISTORY:
 * - 251128_1147 danielbeer: Created field mapping row component
 * 
 * Features:
 * - Field display with type and sample
 * - Mapping arrow with enable/disable toggle
 * - Red highlight for unmapped fields
 * - Source field selector
 * - Transform indicator
 */

import React, { useState, useRef } from 'react';
import type { Field, FieldMapping, DataSet } from '../types/index';
import { usePragmaticContextAware } from '@opcentral/draggr';
import './FieldMappingRow.css';

export interface FieldMappingRowProps {
  field: Field;
  mapping?: FieldMapping;
  previousDataset?: DataSet;
  showFieldType: boolean;
  showSampleData: boolean;
  propagatedValue?: any;
  compact: boolean;
  onMappingChange?: (updates: Partial<FieldMapping>) => void;
  onFieldFocus?: () => void;
  onReorder?: (fieldId: string, newIndex: number) => void;
  onCreateMapping?: (sourceFieldId: string, targetFieldId: string) => void;
  datasetId: string;
  index?: number;
}

/**
 * FieldMappingRow - Single field with mapping controls
 */
export const FieldMappingRow: React.FC<FieldMappingRowProps> = ({
  field,
  mapping,
  previousDataset,
  showFieldType,
  showSampleData,
  propagatedValue,
  compact,
  onMappingChange,
  onFieldFocus,
  onReorder,
  onCreateMapping,
  datasetId,
  index
}) => {
  const [showSourceSelector, setShowSourceSelector] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isDropTarget, setIsDropTarget] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);

  // Setup context-aware drag and drop
  usePragmaticContextAware(rowRef, {
    contextId: datasetId,
    type: 'field',
    data: { 
      fieldId: field.id, 
      index, 
      fieldName: field.name,
      fieldType: field.type
    },
    onDragStart: () => setIsDragging(true),
    onDragEnd: () => setIsDragging(false),
    onDragEnter: () => setIsDropTarget(true),
    onDragLeave: () => setIsDropTarget(false),
    
    // Same dataset - reorder fields
    onSameContextDrop: (source, target) => {
      setIsDropTarget(false);
      const sourceIndex = source.index as number;
      const sourceFieldId = source.fieldId as string;
      
      if (sourceIndex !== index && onReorder && index !== undefined) {
        onReorder(sourceFieldId, index);
      }
    },
    
    // Different dataset - create mapping
    onDifferentContextDrop: (source, target) => {
      setIsDropTarget(false);
      const sourceFieldId = source.fieldId as string;
      
      if (onCreateMapping) {
        onCreateMapping(sourceFieldId, field.id);
      }
    },
  });

  const hasMatch = mapping?.hasMatch ?? false;
  const isEnabled = mapping?.enabled ?? false;
  const hasTransform = !!mapping?.transform;

  // Toggle mapping enabled/disabled
  const handleToggleMapping = () => {
    if (!mapping || !onMappingChange) return;
    onMappingChange({ enabled: !isEnabled });
  };

  // Select source field
  const handleSelectSource = (sourceFieldId: string | null) => {
    if (!mapping || !onMappingChange) return;
    onMappingChange({
      sourceFieldId,
      enabled: sourceFieldId !== null,
      hasMatch: sourceFieldId !== null
    });
    setShowSourceSelector(false);
  };

  // Get display value
  const displayValue = propagatedValue !== undefined 
    ? propagatedValue 
    : (showSampleData && field.sample !== undefined ? field.sample : null);

  return (
    <div 
      ref={rowRef}
      className={`field-mapping-row ${!hasMatch && previousDataset ? 'field-mapping-row--no-match' : ''} ${compact ? 'field-mapping-row--compact' : ''} ${isDragging ? 'field-mapping-row--dragging' : ''} ${isDropTarget ? 'field-mapping-row--drop-target' : ''}`}
      onClick={onFieldFocus}
    >
      {/* Mapping arrow (only show if not first dataset) */}
      {previousDataset && mapping && (
        <div className="field-mapping-row__arrow-container">
          <button
            className={`field-mapping-row__arrow ${isEnabled ? 'field-mapping-row__arrow--enabled' : ''} ${!hasMatch ? 'field-mapping-row__arrow--no-match' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              handleToggleMapping();
            }}
            title={isEnabled ? 'Disable mapping' : 'Enable mapping'}
          >
            {isEnabled ? '→' : '○'}
          </button>
          
          {/* Source field selector */}
          {isEnabled && (
            <button
              className="field-mapping-row__source-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowSourceSelector(!showSourceSelector);
              }}
              title="Select source field"
            >
              {mapping.sourceFieldId 
                ? previousDataset.fields.find(f => f.id === mapping.sourceFieldId)?.name || '?'
                : '?'
              }
            </button>
          )}
          
          {/* Source selector dropdown */}
          {showSourceSelector && (
            <div className="field-mapping-row__source-selector">
              <div className="field-mapping-row__source-list">
                <button
                  className="field-mapping-row__source-option"
                  onClick={() => handleSelectSource(null)}
                >
                  (None)
                </button>
                {previousDataset.fields.map(sourceField => (
                  <button
                    key={sourceField.id}
                    className={`field-mapping-row__source-option ${mapping.sourceFieldId === sourceField.id ? 'field-mapping-row__source-option--selected' : ''}`}
                    onClick={() => handleSelectSource(sourceField.id)}
                  >
                    {sourceField.name}
                    {showFieldType && <span className="field-mapping-row__source-type">{sourceField.type}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Field info */}
      <div className="field-mapping-row__field">
        <div className="field-mapping-row__header">
          <span className="field-mapping-row__name">
            {field.name}
            {field.required && <span className="field-mapping-row__required">*</span>}
          </span>
          {showFieldType && (
            <span className="field-mapping-row__type">
              {field.type}
              {hasTransform && <span className="field-mapping-row__transform-badge">T</span>}
            </span>
          )}
        </div>
        
        {!compact && field.description && (
          <div className="field-mapping-row__description">
            {field.description}
          </div>
        )}
        
        {showSampleData && displayValue !== null && displayValue !== undefined && (
          <div className="field-mapping-row__sample">
            <code>{typeof displayValue === 'object' ? JSON.stringify(displayValue) : String(displayValue)}</code>
          </div>
        )}
      </div>
    </div>
  );
};
